import { useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { X, Check, Loader2, ShoppingCart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function PayPalCheckoutModal({ product, productType, extraOrderFields, onClose, onSuccess, onBeforeCapture }) {
  const [status, setStatus] = useState('idle'); // idle, processing, success, error
  const [errorMsg, setErrorMsg] = useState('');
  const [capturedOrderId, setCapturedOrderId] = useState('');

  if (!product) return null;

  const price = product.price || 0;
  const name = product.name || product.title || 'Produit';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        onClick={(e) => e.target === e.currentTarget && status !== 'processing' && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
          data-testid="paypal-checkout-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <ShoppingCart size={18} className="text-yellow-400" />
              <h3 className="text-lg font-bold text-white">Payer avec PayPal</h3>
            </div>
            {status !== 'processing' && (
              <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="close-paypal-modal">
                <X size={20} />
              </button>
            )}
          </div>

          {/* Product Summary */}
          <div className="p-4 bg-zinc-800/50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-semibold text-sm">{name}</p>
                <p className="text-zinc-500 text-xs">{product.description || product.genre || ''}</p>
              </div>
              <span className="text-2xl font-black text-yellow-400">{price.toFixed(2)}€</span>
            </div>
          </div>

          {/* PayPal Buttons or Status */}
          <div className="p-4">
            {status === 'success' ? (
              <div className="text-center py-6" data-testid="paypal-success">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check size={32} className="text-green-400" />
                </div>
                <p className="text-white font-bold text-lg">Paiement reussi !</p>
                <p className="text-zinc-400 text-sm mt-1">Merci pour votre achat.</p>
                <button onClick={() => { onSuccess?.(capturedOrderId); onClose(); }}
                  className="mt-4 px-6 py-2 bg-green-500 text-black font-bold rounded-full text-sm"
                  data-testid="paypal-success-close">
                  Fermer
                </button>
              </div>
            ) : status === 'error' ? (
              <div className="text-center py-6" data-testid="paypal-error">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-red-500/20 flex items-center justify-center">
                  <X size={32} className="text-red-400" />
                </div>
                <p className="text-white font-bold">Erreur de paiement</p>
                <p className="text-zinc-400 text-sm mt-1">{errorMsg || 'Veuillez reessayer.'}</p>
                <button onClick={() => setStatus('idle')}
                  className="mt-4 px-6 py-2 bg-zinc-700 text-white font-bold rounded-full text-sm">
                  Reessayer
                </button>
              </div>
            ) : status === 'processing' ? (
              <div className="text-center py-8">
                <Loader2 size={32} className="animate-spin text-yellow-400 mx-auto mb-3" />
                <p className="text-white font-semibold">Traitement du paiement...</p>
              </div>
            ) : (
              <PayPalScriptProvider options={{
                clientId: PAYPAL_CLIENT_ID,
                currency: 'EUR',
                intent: 'capture'
              }}>
                <PayPalButtons
                  style={{
                    layout: 'vertical',
                    color: 'gold',
                    shape: 'rect',
                    label: 'pay',
                    height: 45
                  }}
                  data-testid="paypal-buttons"
                  createOrder={async () => {
                    setStatus('processing');
                    try {
                      const res = await fetch(`${API}/paypal/create-order`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          product_id: product.id,
                          product_type: productType || 'product',
                          currency: 'EUR',
                          ...(extraOrderFields || {})
                        })
                      });
                      const data = await res.json();
                      if (data.id) {
                        setStatus('idle');
                        return data.id;
                      }
                      throw new Error(data.detail || 'Erreur creation commande');
                    } catch (err) {
                      setStatus('error');
                      setErrorMsg(err.message);
                      throw err;
                    }
                  }}
                  onApprove={async (data) => {
                    setStatus('processing');
                    try {
                      const res = await fetch(`${API}/paypal/capture-order/${data.orderID}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' }
                      });
                      const result = await res.json();
                      if (result.status === 'COMPLETED') {
                        setCapturedOrderId(data.orderID);
                        // Optional post-capture hook (e.g. register booking)
                        if (typeof onBeforeCapture === 'function') {
                          try { await onBeforeCapture(data.orderID, result); } catch (e) { console.error('post-capture hook failed', e); }
                        }
                        setStatus('success');
                      } else {
                        setStatus('error');
                        setErrorMsg('Le paiement n\'a pas ete complete.');
                      }
                    } catch (err) {
                      setStatus('error');
                      setErrorMsg(err.message);
                    }
                  }}
                  onCancel={() => {
                    setStatus('idle');
                  }}
                  onError={(err) => {
                    console.error('PayPal error:', err);
                    setStatus('error');
                    setErrorMsg('Erreur PayPal. Verifiez votre connexion.');
                  }}
                />
              </PayPalScriptProvider>
            )}
          </div>

          {/* Footer */}
          {status === 'idle' && (
            <div className="px-4 pb-4 text-center">
              <p className="text-zinc-600 text-[10px]">Paiement securise via PayPal. L'argent est envoye directement au compte KAZAJAH STUDIO.</p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
